/**
 * @description Shared company create/edit form used by Company Settings
 *
 * Key features:
 * - Business type dropdown from shared BUSINESS_TYPES (including nonprofit/other)
 * - EIN, address, contact, accounting method
 *
 * @dependencies
 * - constants/businessTypes: canonical entity-type options
 *
 * @notes
 * - Settings → Edit is the post-onboarding path to change businessType
 * - EIN is required only when requireEin is true (create). Settings pass false
 *   so onboarded companies with no EIN can still change business type.
 * - Timezone is required only when requireTimezone is true (TowTech).
 *   LynxPay settings pass false. Blank does not write a zone. The field
 *   stays visible. Do not invent America/New_York or America/Phoenix.
 * - Merchant Alias is editable when showMerchantAlias is true (Elavon assigned).
 *   Writes go through PUT /companies/me and require company.edit.
 *   Wrapper id="merchant-alias" is the Add Alias deep-link scroll target.
 * - Business Email helper describes the field as outbound reply-to. Outreach
 *   email_sender.reply_to is an optional override, not a second required field.
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { BUSINESS_TYPES } from '../../../constants/businessTypes';
import { US_IANA_TIMEZONES, timezoneForUsState } from '../../../constants/usIanaTimezones';

// US States for dropdown
const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
];

// Fiscal Year Start months
const FISCAL_MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

// Accounting Methods
const ACCOUNTING_METHODS = [
  {
    value: 'cash',
    label: 'Cash',
    description: 'Record income when received, expenses when paid'
  },
  {
    value: 'accrual',
    label: 'Accrual',
    description: 'Record income when earned, expenses when incurred'
  }
];

// Form field defaults - defined outside component to avoid recreation
const FORM_DEFAULTS = {
  businessName: '',
  ein: '',
  businessType: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'US',
  phone: '',
  email: '',
  website: '',
  fiscalYearStart: 1,
  accountingMethod: '',
  timezone: '',
  industryType: 'general_business',
  merchantAlias: '',
};

// Sanitize data - convert null/undefined values to defaults
const sanitizeData = (data) => {
  return Object.keys(FORM_DEFAULTS).reduce((acc, key) => {
    acc[key] = data[key] ?? FORM_DEFAULTS[key];
    return acc;
  }, {});
};

/**
 * Reusable Company Form Component
 * Can be used in company onboarding, settings, welcome checklist, etc.
 */
const CompanyForm = ({
  onSubmit,
  initialData = {},
  submitLabel = 'Create Company',
  showTitle = true,
  loading = false,
  error = null,
  onDirtyChange = null,
  requireEin = true,
  requireTimezone = true,
  showMerchantAlias = false
}) => {
  // Form state
  const [formData, setFormData] = useState(() => sanitizeData(initialData));
  const [originalData, setOriginalData] = useState(() => sanitizeData(initialData));

  // Validation errors
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showValidationSummary, setShowValidationSummary] = useState(false);

  // Check if form has unsaved changes
  const isDirty = JSON.stringify(formData) !== JSON.stringify(originalData);

  // Notify parent when dirty state changes
  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(isDirty);
    }
  }, [isDirty, onDirtyChange]);

  // Sync form data when initialData changes (e.g., when company data loads)
  useEffect(() => {
    const sanitized = sanitizeData(initialData);
    setFormData(sanitized);
    setOriginalData(sanitized);
    setErrors({});
    setTouched({});
    setShowValidationSummary(false);
  }, [initialData]);

  // Format EIN as user types (XX-XXXXXXX)
  const formatEIN = (value) => {
    // Remove all non-digits
    const cleaned = value.replace(/\D/g, '');
    
    // Format as XX-XXXXXXX
    if (cleaned.length <= 2) {
      return cleaned;
    } else {
      return cleaned.slice(0, 2) + '-' + cleaned.slice(2, 9);
    }
  };

  // Validate EIN format
  const validateEIN = (ein) => {
    const einRegex = /^\d{2}-\d{7}$/;
    return einRegex.test(ein);
  };

  // Validate postal code
  const validatePostalCode = (postalCode) => {
    const postalCodeRegex = /^\d{5}(-\d{4})?$/;
    return postalCodeRegex.test(postalCode);
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for EIN
    if (name === 'ein') {
      setFormData(prev => ({
        ...prev,
        [name]: formatEIN(value)
      }));
    } else {
      setFormData(prev => {
        const next = { ...prev, [name]: value };
        if (name === 'state' && !prev.timezone) {
          const suggested = timezoneForUsState(value);
          if (suggested) next.timezone = suggested;
        }
        return next;
      });
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Handle blur for validation
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    validateField(name, formData[name]);
  };

  // Validate single field
  const validateField = (name, value) => {
    let error = null;

    switch (name) {
      case 'businessName':
        if (!value.trim()) error = 'Business name is required';
        break;
      case 'ein':
        if (requireEin && !value) {
          error = 'EIN is required';
        } else if (value && !validateEIN(value)) {
          error = 'EIN must be in format XX-XXXXXXX';
        }
        break;
      case 'businessType':
        if (!value) error = 'Business type is required';
        break;
      case 'addressLine1':
        if (!value.trim()) error = 'Address line 1 is required';
        break;
      case 'city':
        if (!value.trim()) error = 'City is required';
        break;
      case 'state':
        if (!value) error = 'State is required';
        break;
      case 'postalCode':
        if (!value) {
          error = 'Postal code is required';
        } else if (!validatePostalCode(value)) {
          error = 'Invalid postal code format';
        }
        break;
      case 'phone':
        if (value) {
          const digits = value.replace(/\D/g, '');
          if (digits.length !== 10) {
            error = 'Phone number must be 10 digits';
          }
        }
        break;
      case 'email':
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = 'Enter a valid email address';
        }
        break;
      case 'website':
        if (value && !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i.test(value)) {
          error = 'Enter a valid website URL';
        }
        break;
      case 'accountingMethod':
        if (!value) error = 'Accounting method is required';
        break;
      case 'timezone':
        if (requireTimezone && !value) error = 'Timezone is required';
        break;
      default:
        break;
    }

    setErrors(prev => ({
      ...prev,
      [name]: error
    }));

    return !error;
  };

  // Field labels for validation summary
  const fieldLabels = {
    businessName: 'Business Name',
    ein: 'EIN (Federal Tax ID)',
    businessType: 'Business Type',
    addressLine1: 'Address Line 1',
    city: 'City',
    state: 'State',
    postalCode: 'Zip Code',
    phone: 'Phone Number',
    email: 'Business Email',
    website: 'Website',
    accountingMethod: 'Accounting Method',
    timezone: 'Timezone'
  };

  // Validate entire form
  const validateForm = () => {
    const requiredFields = [
      'businessName',
      ...(requireEin ? ['ein'] : []),
      'businessType',
      'addressLine1', 'city', 'state', 'postalCode',
      'accountingMethod',
      ...(requireTimezone ? ['timezone'] : [])
    ];

    let isValid = true;
    const newErrors = {};

    // Validate each field synchronously
    requiredFields.forEach(field => {
      const value = formData[field];
      let fieldError = null;

      switch (field) {
        case 'businessName':
          if (!value?.trim()) fieldError = 'Business name is required';
          break;
        case 'ein':
          if (requireEin && !value) {
            fieldError = 'EIN is required';
          } else if (value && !validateEIN(value)) {
            fieldError = 'EIN must be in format XX-XXXXXXX';
          }
          break;
        case 'businessType':
          if (!value) fieldError = 'Business type is required';
          break;
        case 'addressLine1':
          if (!value?.trim()) fieldError = 'Address line 1 is required';
          break;
        case 'city':
          if (!value?.trim()) fieldError = 'City is required';
          break;
        case 'state':
          if (!value) fieldError = 'State is required';
          break;
        case 'postalCode':
          if (!value) {
            fieldError = 'Postal code is required';
          } else if (!validatePostalCode(value)) {
            fieldError = 'Invalid postal code format';
          }
          break;
        case 'accountingMethod':
          if (!value) fieldError = 'Accounting method is required';
          break;
        case 'timezone':
          if (requireTimezone && !value) fieldError = 'Timezone is required';
          break;
        default:
          break;
      }

      if (fieldError) {
        isValid = false;
        newErrors[field] = fieldError;
      }
    });

    if (!requireEin && formData.ein && !validateEIN(formData.ein)) {
      isValid = false;
      newErrors.ein = 'EIN must be in format XX-XXXXXXX';
    }

    setErrors(newErrors);
    setTouched(requiredFields.reduce((acc, field) => ({
      ...acc,
      [field]: true
    }), {}));
    setShowValidationSummary(!isValid);

    // Scroll to first error field if validation failed
    if (!isValid) {
      const firstErrorField = requiredFields.find(field => newErrors[field]);
      if (firstErrorField) {
        const element = document.getElementById(firstErrorField);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
        }
      }
    }

    return isValid;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      const payload = { ...formData };
      if (!payload.ein) {
        delete payload.ein;
      }
      if (showMerchantAlias) {
        payload.merchantAlias = (payload.merchantAlias || '').trim();
      } else {
        delete payload.merchantAlias;
      }
      if (!requireTimezone && !payload.timezone) {
        delete payload.timezone;
      }
      onSubmit(payload);
    }
  };

  // Field error component
  const FieldError = ({ name }) => {
    if (!touched[name] || !errors[name]) return null;
    return (
      <p className="mt-1 text-sm text-red-600">
        {errors[name]}
      </p>
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      {showTitle && (
        <div className="mb-4">
          <h2 className="text-xl font-bold text-gray-900">Company Information</h2>
          <p className="mt-1 text-sm text-gray-600">
            Set up your business information for tax and compliance purposes
          </p>
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Validation Summary - shows missing required fields */}
      {showValidationSummary && Object.keys(errors).length > 0 && (
        <div className="rounded-md bg-amber-50 border border-amber-200 p-4 mb-4">
          <p className="text-sm font-medium text-amber-800 mb-2">Please complete the following required fields:</p>
          <ul className="text-sm text-amber-700 list-disc list-inside space-y-1">
            {Object.keys(errors).map(field => (
              <li key={field}>{fieldLabels[field] || field}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Business Information Section */}
      <div className="mb-4">
        
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Business Name */}
          <div className="md:col-span-2">
            <label htmlFor="businessName" className="block text-gray-700 font-medium mb-2">
              Business Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="businessName"
              id="businessName"
              value={formData.businessName}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`mt-1 block w-full p-2 rounded-lg border ${
                touched.businessName && errors.businessName
                  ? 'border-red-500'
                  : 'border-gray-300'
              }`}
              placeholder="Your Company LLC"
            />
            <FieldError name="businessName" />
          </div>

          {/* EIN */}
          <div>
            <label htmlFor="ein" className="block text-gray-700 font-medium mb-2">
              EIN (Federal Tax ID){requireEin && <span className="text-red-500"> *</span>}
            </label>
            <input
              type="text"
              name="ein"
              id="ein"
              value={formData.ein}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength="10"
              className={`mt-1 block w-full p-2 rounded-lg border ${
                touched.ein && errors.ein
                  ? 'border-red-500'
                  : 'border-gray-300'
              }`}
              placeholder="XX-XXXXXXX"
            />
            <FieldError name="ein" />
          </div>

          {/* Business Type */}
          <div>
            <label htmlFor="businessType" className="block text-gray-700 font-medium mb-2">
              Business Type <span className="text-red-500">*</span>
            </label>
            <select
              name="businessType"
              id="businessType"
              value={formData.businessType}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`mt-1 block w-full p-2 rounded-lg border ${
                touched.businessType && errors.businessType
                  ? 'border-red-500'
                  : 'border-gray-300'
              }`}
            >
              <option value="">Select business type</option>
              {BUSINESS_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <FieldError name="businessType" />
          </div>
        </div>
      </div>

      {/* Business Address Section */}
      <div className="mb-4">
        
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Address Line 1 */}
          <div className="md:col-span-2">
            <label htmlFor="addressLine1" className="block text-gray-700 font-medium mb-2">
              Address Line 1 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="addressLine1"
              id="addressLine1"
              value={formData.addressLine1}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`mt-1 block w-full p-2 rounded-lg border ${
                touched.addressLine1 && errors.addressLine1
                  ? 'border-red-500'
                  : 'border-gray-300'
              }`}
              placeholder="123 Main Street"
            />
            <FieldError name="addressLine1" />
          </div>

          {/* Address Line 2 */}
          <div className="md:col-span-2">
            <label htmlFor="addressLine2" className="block text-gray-700 font-medium mb-2">
              Address Line 2
            </label>
            <input
              type="text"
              name="addressLine2"
              id="addressLine2"
              value={formData.addressLine2}
              onChange={handleChange}
              className="mt-1 block w-full p-2 rounded-lg border border-gray-300"
              placeholder="Suite 200, Apt 5B, Unit C (optional)"
            />
          </div>

          {/* City */}
          <div>
            <label htmlFor="city" className="block text-gray-700 font-medium mb-2">
              City <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="city"
              id="city"
              value={formData.city}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`mt-1 block w-full p-2 rounded-lg border ${
                touched.city && errors.city
                  ? 'border-red-500'
                  : 'border-gray-300'
              }`}
              placeholder="New York"
            />
            <FieldError name="city" />
          </div>

          {/* State */}
          <div>
            <label htmlFor="state" className="block text-gray-700 font-medium mb-2">
              State <span className="text-red-500">*</span>
            </label>
            <select
              name="state"
              id="state"
              value={formData.state}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`mt-1 block w-full p-2 rounded-lg border ${
                touched.state && errors.state
                  ? 'border-red-500'
                  : 'border-gray-300'
              }`}
            >
              <option value="">Select state</option>
              {US_STATES.map(state => (
                <option key={state.value} value={state.value}>
                  {state.label}
                </option>
              ))}
            </select>
            <FieldError name="state" />
          </div>

          {/* Postal Code */}
          <div>
            <label htmlFor="postalCode" className="block text-gray-700 font-medium mb-2">
              Zip Code <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="postalCode"
              id="postalCode"
              value={formData.postalCode}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength="10"
              className={`mt-1 block w-full p-2 rounded-lg border ${
                touched.postalCode && errors.postalCode
                  ? 'border-red-500'
                  : 'border-gray-300'
              }`}
              placeholder="10001"
            />
            <FieldError name="postalCode" />
          </div>
        </div>
      </div>

      {/* Contact Information — source of truth for invoice/estimate From + PDFs */}
      <div className="mb-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              name="phone"
              id="phone"
              value={formData.phone}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`mt-1 block w-full p-2 rounded-lg border ${
                touched.phone && errors.phone
                  ? 'border-red-500'
                  : 'border-gray-300'
              }`}
              placeholder="(555) 123-4567"
            />
            <FieldError name="phone" />
          </div>

          <div>
            <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
              Business Email
            </label>
            <input
              type="email"
              name="email"
              id="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`mt-1 block w-full p-2 rounded-lg border ${
                touched.email && errors.email
                  ? 'border-red-500'
                  : 'border-gray-300'
              }`}
              placeholder="billing@example.com"
            />
            <p className="text-xs text-gray-500 mt-1">
              Your reply-to email used in outbound emails.
            </p>
            <FieldError name="email" />
          </div>

          <div className="md:col-span-2">
            <label htmlFor="website" className="block text-gray-700 font-medium mb-2">
              Website
            </label>
            <input
              type="url"
              name="website"
              id="website"
              value={formData.website}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`mt-1 block w-full p-2 rounded-lg border ${
                touched.website && errors.website
                  ? 'border-red-500'
                  : 'border-gray-300'
              }`}
              placeholder="https://example.com"
            />
            <FieldError name="website" />
          </div>
        </div>
      </div>

      {showMerchantAlias && (
        <div className="mb-4" id="merchant-alias">
          <div>
            <label htmlFor="merchantAlias" className="block text-gray-700 font-medium mb-2">
              Merchant Alias
            </label>
            <input
              type="text"
              name="merchantAlias"
              id="merchantAlias"
              value={formData.merchantAlias}
              onChange={handleChange}
              onBlur={handleBlur}
              className="mt-1 block w-full p-2 rounded-lg border border-gray-300"
              placeholder="Merchant alias"
              autoComplete="off"
            />
            <p className="mt-1 text-xs text-gray-500">
              You'll receive an email with the Merchant Alias once your application is accepted.
            </p>
          </div>
        </div>
      )}

      {/* Accounting Settings Section */}
      <div className="mb-4">
        
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Fiscal Year Start */}
          <div>
            <label htmlFor="fiscalYearStart" className="block text-gray-700 font-medium mb-2">
              Fiscal Year Start
            </label>
            <select
              name="fiscalYearStart"
              id="fiscalYearStart"
              value={formData.fiscalYearStart}
              onChange={handleChange}
              className="mt-1 block w-full p-2 rounded-lg border border-gray-300"
            >
              {FISCAL_MONTHS.map(month => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              When your fiscal year begins
            </p>
          </div>

          {/* Accounting Method */}
          <div>
            <label htmlFor="accountingMethod" className="block text-gray-700 font-medium mb-2">
              Accounting Method <span className="text-red-500">*</span>
            </label>
            <select
              name="accountingMethod"
              id="accountingMethod"
              value={formData.accountingMethod}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`mt-1 block w-full p-2 rounded-lg border ${
                touched.accountingMethod && errors.accountingMethod
                  ? 'border-red-500'
                  : 'border-gray-300'
              }`}
            >
              <option value="">Select method</option>
              {ACCOUNTING_METHODS.map(method => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>
            {formData.accountingMethod && (
              <p className="mt-1 text-xs text-gray-500">
                {ACCOUNTING_METHODS.find(m => m.value === formData.accountingMethod)?.description}
              </p>
            )}
            <FieldError name="accountingMethod" />
          </div>

          <div>
            <label htmlFor="timezone" className="block text-gray-700 font-medium mb-2">
              Timezone{requireTimezone && <span className="text-red-500"> *</span>}
            </label>
            <select
              name="timezone"
              id="timezone"
              value={formData.timezone}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`mt-1 block w-full p-2 rounded-lg border ${
                touched.timezone && errors.timezone
                  ? 'border-red-500'
                  : 'border-gray-300'
              }`}
            >
              <option value="">Select timezone</option>
              {US_IANA_TIMEZONES.map((zone) => (
                <option key={zone.value} value={zone.value}>
                  {zone.label}
                </option>
              ))}
            </select>
            <FieldError name="timezone" />
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end space-x-2">
        <button
          type="submit"
          disabled={loading}
          className={`px-6 py-2.5 rounded-lg font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2
            ${loading
              ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
              : 'bg-brand text-white hover:bg-brand-hover shadow-sm hover:shadow-md'
            }`}
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </span>
          ) : submitLabel}
        </button>
      </div>
    </form>
  );
};

CompanyForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  submitLabel: PropTypes.string,
  showTitle: PropTypes.bool,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onDirtyChange: PropTypes.func,
  requireEin: PropTypes.bool,
  requireTimezone: PropTypes.bool,
  showMerchantAlias: PropTypes.bool
};

export default CompanyForm;